// Registers the service worker and turns web push on or off for this browser. The settings page
// (livewire/account/notifications) drives it through window.push and listens for 'push-changed'.
import { ask, worked } from './permissions.js';   // a card before the browser's own prompt

const meta = (name) => document.querySelector(`meta[name="${name}"]`)?.getAttribute('content');

if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
        try {
            await navigator.serviceWorker.register('/build/sw.js', {scope: '/'});
        } catch (e) {
            return;     // e.g. a server without the Service-Worker-Allowed header (local copies)
        }

        // Tells the worker who's signed in, so pages kept for one person are never shown to the next
        (await navigator.serviceWorker.ready).active?.postMessage({type: 'session-user', user: meta('session-user') ?? ''});

        renew().catch((error) => console.error('Push:', error));
    });
}

/*
 * The subscription this browser saved, and for whom (localStorage 'push': {user, endpoint}). Browsers swap a device's
 * subscription now and then (sw.js pushsubscriptionchange); the next page the same person opens saves the new one in
 * place of the old, so the device keeps getting their notifications. Someone else signed in here never takes it over:
 * they turn push on themselves.
 */
const SAVED = 'push';
const saved = () => { try { return JSON.parse(localStorage.getItem(SAVED)) ?? {}; } catch { return {}; } };
const remember = (endpoint) => { try { localStorage.setItem(SAVED, JSON.stringify({user: meta('session-user'), endpoint})); } catch {} };

async function renew() {
    const user = meta('session-user');
    if (!user || !('PushManager' in window) || Notification.permission !== 'granted') {
        return;
    }
    const subscription = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
    const last = saved();

    if (!subscription || subscription.endpoint === last.endpoint) {
        return;
    }
    if (last.user !== user) {
        // Not saved here by this person (or before this was kept): only noted
        return last.user ? undefined : remember(subscription.endpoint);
    }

    await save(subscription, last.endpoint);
}

async function save(subscription, replaces = null) {
    await post('/notification/push/subscribe', {
        endpoint: subscription.endpoint,
        public_key: base64(subscription.getKey('p256dh')),
        auth_token: base64(subscription.getKey('auth')),
        encoding: (PushManager.supportedContentEncodings || ['aes128gcm']).includes('aes128gcm') ? 'aes128gcm' : 'aesgcm',
        replaces,
    });
    remember(subscription.endpoint);
}

async function post(url, data) {
    const response = await fetch(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': meta('csrf-token')},
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error(`${url} answered ${response.status}`);
    }

    return response.json();
}

function base64(buffer) {
    return buffer ? btoa(String.fromCharCode(...new Uint8Array(buffer))) : null;
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const raw = atob((base64String + padding).replace(/-/g, '+').replace(/_/g, '/'));

    return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

function changed(state) {
    window.dispatchEvent(new CustomEvent('push-changed', {detail: state}));

    return state;
}

window.push = {
    /** 'unsupported' (no push here; on iPhone, add the site to the Home Screen first), 'denied', 'on' or 'off'. */
    async state() {
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            return 'unsupported';
        }
        if (Notification.permission === 'denied') {
            return 'denied';
        }

        const registration = await navigator.serviceWorker.ready;

        return (await registration.pushManager.getSubscription()) ? 'on' : 'off';
    },

    /** quiet: offered without a tap (after sign-in), so nothing for 30 days after a Not Now */
    async enable({ quiet = false } = {}) {
        try {
            if (!await ask('notifications', { quiet })) {
                return changed(await this.state());
            }
            if (await Notification.requestPermission() !== 'granted') {
                Toaster.warning('Notifications are blocked for this site in your browser settings.');

                return changed(await this.state());
            }

            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription()
                ?? await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(meta('vapid-public-key')),
                });

            await save(subscription);
            worked('notifications');

            Toaster.success('Notifications are on for this device.');

            return changed('on');
        } catch (error) {
            console.error('Push:', error);
            Toaster.error('Notifications could not be turned on for this device.');

            return changed(await this.state());
        }
    },

    async disable() {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await post('/notification/push/unsubscribe', {endpoint: subscription.endpoint});
                await subscription.unsubscribe();
                remember(null);
            }

            Toaster.info('Notifications are off for this device.');

            return changed('off');
        } catch (error) {
            console.error('Push:', error);
            Toaster.error('Notifications could not be turned off for this device.');

            return changed(await this.state());
        }
    },
};
